import axios from 'axios';
import { API_URL } from './config';

// Configure axios defaults for better reliability
axios.defaults.timeout = 30000; // 30 second default timeout (increased from 15s)
axios.defaults.headers.common['Content-Type'] = 'application/json';

const API = API_URL;

// ============== INVITE LINK SYSTEM ==============

export const validateInviteCode = async (code) => {
  const response = await axios.get(`${API}/invite/validate/${code}`);
  return response.data;
};

export const registerWithInvite = async (inviteCode, email, password, name, phone = null) => {
  const response = await axios.post(`${API}/auth/register-with-invite`, {
    invite_code: inviteCode,
    email,
    password,
    name,
    phone
  });
  return response.data;
};

// Admin - Invite Links
export const getInviteLinks = async () => {
  const response = await axios.get(`${API}/admin/invite-links`);
  return response.data;
};

export const createInviteLink = async (clientName, clientPhone = null, brokerAccount = null, notes = null, expiresDays = 30, maxUses = 1, grantsPremium = false, premiumDays = 30) => {
  const response = await axios.post(`${API}/admin/invite-links`, {
    client_name: clientName,
    client_phone: clientPhone,
    broker_account: brokerAccount,
    notes: notes,
    expires_days: expiresDays,
    max_uses: maxUses,
    grants_premium: grantsPremium,
    premium_days: premiumDays
  });
  return response.data;
};

export const revokeInviteLink = async (linkId) => {
  const response = await axios.delete(`${API}/admin/invite-links/${linkId}`);
  return response.data;
};

export const extendInviteLink = async (linkId, days = 7) => {
  const response = await axios.put(`${API}/admin/invite-links/${linkId}/extend?days=${days}`);
  return response.data;
};

// ============== EXCHANGE RATE ==============

// Exchange Rate
export const getExchangeRate = async () => {
  const response = await axios.get(`${API}/exchange-rate`);
  return response.data;
};

// User Status
export const getUserStatus = async () => {
  const response = await axios.get(`${API}/user/status`);
  return response.data;
};

export const activateUser = async (brokerAccount) => {
  const response = await axios.post(`${API}/user/activate`, { broker_account: brokerAccount });
  return response.data;
};

export const updateUserSettings = async (settings) => {
  const response = await axios.put(`${API}/user/settings`, settings);
  return response.data;
};

// ============== GUIDE PROGRESS ==============

export const getGuideProgress = async () => {
  const response = await axios.get(`${API}/user/guide-progress`);
  return response.data;
};

export const saveGuideProgress = async (progress) => {
  const response = await axios.post(`${API}/user/guide-progress`, progress);
  return response.data;
};

// Trading Journal
export const getJournalEntries = async (accountId = null) => {
  const params = accountId ? { account_id: accountId } : {};
  const response = await axios.get(`${API}/journal`, { params });
  return response.data;
};

export const createJournalEntry = async (entry) => {
  const response = await axios.post(`${API}/journal`, entry);
  return response.data;
};

export const deleteJournalEntry = async (id) => {
  const response = await axios.delete(`${API}/journal/${id}`);
  return response.data;
};

export const deleteAllJournalEntries = async () => {
  const response = await axios.delete(`${API}/journal`);
  return response.data;
};

// Trading Accounts (Multi-Account)
export const getTradingAccounts = async () => {
  const response = await axios.get(`${API}/accounts`);
  return response.data;
};

export const createTradingAccount = async (account) => {
  const response = await axios.post(`${API}/accounts`, account);
  return response.data;
};

export const updateTradingAccount = async (id, data) => {
  const response = await axios.put(`${API}/accounts/${id}`, data);
  return response.data;
};

export const deleteTradingAccount = async (id) => {
  const response = await axios.delete(`${API}/accounts/${id}`);
  return response.data;
};

// Target Tracker
export const getMonthlyTargets = async () => {
  const response = await axios.get(`${API}/targets`);
  return response.data;
};

export const getCurrentTarget = async () => {
  const response = await axios.get(`${API}/targets/current`);
  return response.data;
};

export const createMonthlyTarget = async (target) => {
  const response = await axios.post(`${API}/targets`, target);
  return response.data;
};

export const deleteMonthlyTarget = async (id) => {
  const response = await axios.delete(`${API}/targets/${id}`);
  return response.data;
};

// Export Data
export const exportJournalCSV = (accountId = null) => {
  const params = accountId ? `?account_id=${accountId}` : '';
  window.open(`${API}/export/journal/csv${params}`, '_blank');
};

export const exportMonthlyReportCSV = (month = null) => {
  const params = month ? `?month=${month}` : '';
  window.open(`${API}/export/report/csv${params}`, '_blank');
};

// Market Data
export const getCurrencyStrength = async () => {
  const response = await axios.get(`${API}/market/currency-strength`);
  return response.data;
};

export const getRinggitStatus = async () => {
  const response = await axios.get(`${API}/market/ringgit-status`);
  return response.data;
};

// Drawdown Alert
export const getDrawdownStatus = async () => {
  const response = await axios.get(`${API}/drawdown/status`);
  return response.data;
};

// Calculators
export const calculateCompounding = async (data) => {
  const response = await axios.post(`${API}/calculator/compounding`, data);
  return response.data;
};

export const calculateBreakeven = async (data) => {
  const response = await axios.post(`${API}/calculator/breakeven`, data);
  return response.data;
};

// Economic Calendar
export const getEconomicEvents = async () => {
  const response = await axios.get(`${API}/calendar/events`);
  return response.data;
};

// Reports
export const getWeeklyReport = async () => {
  const response = await axios.get(`${API}/report/weekly`);
  return response.data;
};

// Email Report
export const sendWeeklyEmailReport = async (email, includeTrades = true, includeAnalytics = true) => {
  const response = await axios.post(`${API}/report/weekly/email`, {
    email,
    include_trades: includeTrades,
    include_analytics: includeAnalytics
  });
  return response.data;
};

// PDF Report
export const generatePDFReport = async (days = 30, includeSummary = true, includeTrades = true, includeAnalytics = true) => {
  const response = await axios.post(`${API}/report/pdf`, {
    days,
    include_summary: includeSummary,
    include_trades: includeTrades,
    include_analytics: includeAnalytics
  });
  return response.data;
};

// Screenshot Upload
export const uploadScreenshot = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API}/screenshot/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const deleteScreenshot = async (fileId) => {
  const response = await axios.delete(`${API}/screenshot/${fileId}`);
  return response.data;
};

// AI Analysis
export const getAIAnalysis = async (periodDays = 30) => {
  // Longer timeout for AI analysis
  const response = await axios.post(`${API}/ai/analyze`, { period_days: periodDays }, {
    timeout: 120000 // 2 minutes
  });
  return response.data;
};

// MT5 Integration - Webhook Keys
export const getWebhookKeys = async () => {
  const response = await axios.get(`${API}/webhook/keys`);
  return response.data;
};

export const createWebhookKey = async (data) => {
  const response = await axios.post(`${API}/webhook/keys`, data);
  return response.data;
};

export const deleteWebhookKey = async (keyId) => {
  const response = await axios.delete(`${API}/webhook/keys/${keyId}`);
  return response.data;
};

export const getEACode = async (apiKey) => {
  const response = await axios.get(`${API}/webhook/ea-code`, { params: { api_key: apiKey } });
  return response.data;
};

// MT5 Statement Import
export const importMT5Statement = async (file, accountId = null) => {
  const formData = new FormData();
  formData.append('file', file);
  if (accountId) {
    formData.append('account_id', accountId);
  }
  const response = await axios.post(`${API}/import/mt5-statement`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000 // v7.6: 2 minutes for large PDF/CSV files
  });
  return response.data;
};

// AI Chat
export const sendChatMessage = async (sessionId, message) => {
  // Use longer timeout for AI chat (multi-timeframe analysis can take 2-3 minutes)
  const response = await axios.post(`${API}/ai/chat`, {
    session_id: sessionId,
    message: message
  }, {
    timeout: 180000 // 3 minutes for AI multi-timeframe analysis
  });
  return response.data;
};

export const getChatHistory = async (sessionId) => {
  const response = await axios.get(`${API}/ai/chat/history/${sessionId}`);
  return response.data;
};

export const clearChatHistory = async (sessionId) => {
  const response = await axios.delete(`${API}/ai/chat/history/${sessionId}`);
  return response.data;
};

// Trading Profile
export const getTradingProfile = async () => {
  const response = await axios.get(`${API}/trading-profile`);
  return response.data;
};

export const saveTradingProfile = async (profile) => {
  const response = await axios.post(`${API}/trading-profile`, profile);
  return response.data;
};

// ============== PHASE 1 FEATURES ==============

// Daily Checklist
export const getTodayChecklist = async () => {
  const response = await axios.get(`${API}/checklist/today`);
  return response.data;
};

export const updateChecklist = async (items) => {
  const response = await axios.put(`${API}/checklist/today`, { items });
  return response.data;
};

export const getChecklistStreak = async () => {
  const response = await axios.get(`${API}/checklist/streak`);
  return response.data;
};

// Session Analysis
export const getSessionAnalysis = async () => {
  const response = await axios.get(`${API}/analysis/sessions`);
  return response.data;
};

// Emotion Analysis
export const getEmotionAnalysis = async () => {
  const response = await axios.get(`${API}/analysis/emotions`);
  return response.data;
};

// Achievements
export const getAchievements = async () => {
  const response = await axios.get(`${API}/achievements`);
  return response.data;
};

// Equity Curve
export const getEquityCurve = async (accountId = null, days = 30) => {
  const params = { days };
  if (accountId) params.account_id = accountId;
  const response = await axios.get(`${API}/equity-curve`, { params });
  return response.data;
};

// Risk Heat Map
export const getRiskHeatMap = async (accountId = null, days = 30) => {
  const params = { days };
  if (accountId) params.account_id = accountId;
  const response = await axios.get(`${API}/risk-heatmap`, { params });
  return response.data;
};

// Backtesting
export const runBacktest = async (rules, startingBalance = 1000, days = 90) => {
  const response = await axios.post(`${API}/backtest`, {
    rules,
    starting_balance: startingBalance,
    days
  });
  return response.data;
};

// Strategy Builder
export const getStrategies = async () => {
  const response = await axios.get(`${API}/strategies`);
  return response.data;
};

export const saveStrategy = async (strategy) => {
  const response = await axios.post(`${API}/strategies`, strategy);
  return response.data;
};

export const updateStrategy = async (strategyId, strategy) => {
  const response = await axios.put(`${API}/strategies/${strategyId}`, strategy);
  return response.data;
};

export const deleteStrategy = async (strategyId) => {
  const response = await axios.delete(`${API}/strategies/${strategyId}`);
  return response.data;
};

export const backtestStrategy = async (strategy) => {
  const response = await axios.post(`${API}/strategies/backtest`, strategy);
  return response.data;
};

// ============== AI SCRIPT GENERATOR ==============

export const generateMarketingScript = async (options = {}) => {
  const response = await axios.post(`${API}/marketing/generate-script`, {
    script_type: options.scriptType || 'ugc_testimonial',
    target_audience: options.targetAudience || 'Trader retail Malaysia yang struggle dengan konsistensi',
    main_pain: options.mainPain || 'Tak dapat konsisten profit, selalu rugi, tak tahu nak analisa macam mana',
    desired_result: options.desiredResult || 'Profit konsisten setiap bulan, confident dengan setiap trade',
    language: options.language || 'bm',
    duration: options.duration || '45-60'
  });
  return response.data;
};

export const getGeneratedScripts = async () => {
  const response = await axios.get(`${API}/marketing/scripts`);
  return response.data;
};

export const deleteGeneratedScript = async (scriptId) => {
  const response = await axios.delete(`${API}/marketing/scripts/${scriptId}`);
  return response.data;
};


// ============== PREMIUM FEATURES APIs ==============

// Get premium status
export const getPremiumStatus = async () => {
  const response = await axios.get(`${API}/premium/status`);
  return response.data;
};

// Activate premium (manual)
export const activatePremium = async (tier = 'premium', months = 1) => {
  const response = await axios.post(`${API}/premium/activate`, {
    user_id: 'default',
    tier,
    duration_months: months,
    payment_method: 'manual'
  });
  return response.data;
};

// Deactivate premium
export const deactivatePremium = async () => {
  const response = await axios.post(`${API}/premium/deactivate`);
  return response.data;
};

// ============== STRIPE PAYMENT APIs ==============

// Get available payment packages
export const getPaymentPackages = async () => {
  const response = await axios.get(`${API}/payment/packages`);
  return response.data;
};

// Create Stripe checkout session
export const createCheckoutSession = async (packageId, originUrl = null) => {
  const origin = originUrl || window.location.origin;
  const response = await axios.post(`${API}/payment/checkout`, {
    package_id: packageId,
    origin_url: origin
  });
  return response.data;
};

// Get payment status
export const getPaymentStatus = async (sessionId) => {
  const response = await axios.get(`${API}/payment/status/${sessionId}`);
  return response.data;
};

// Poll payment status (with retry)
export const pollPaymentStatus = async (sessionId, maxAttempts = 5, interval = 2000) => {
  for (let i = 0; i < maxAttempts; i++) {
    try {
      const status = await getPaymentStatus(sessionId);
      if (status.payment_status === 'paid' || status.status === 'expired') {
        return status;
      }
      await new Promise(resolve => setTimeout(resolve, interval));
    } catch (error) {
      console.error('Error polling payment status:', error);
    }
  }
  return { status: 'timeout', payment_status: 'unknown' };
};

// AI Smart Signals
export const getAISignals = async () => {
  const response = await axios.get(`${API}/premium/signals`);
  return response.data;
};

export const getSignalPairs = async () => {
  const response = await axios.get(`${API}/premium/signals/pairs`);
  return response.data;
};

export const generateSignalForPair = async (pair, timeframe = 'H4', language = 'bm', forceRefresh = false) => {
  // Use longer timeout for signal generation (AI calls take time)
  // AbortController to allow cancellation
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 180000); // 180s hard timeout (increased from 150s)
  
  try {
    const response = await axios.post(`${API}/premium/signals/generate`, {
      pair,
      timeframe,
      hybrid: true,
      language: language,
      force_refresh: forceRefresh  // NEW: Force generate fresh signal
    }, {
      timeout: 150000, // 150 seconds for AI signal generation (increased from 120s)
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return response.data;
  } catch (error) {
    clearTimeout(timeoutId);
    // Rethrow with more specific message
    if (error.name === 'AbortError' || error.code === 'ECONNABORTED') {
      throw new Error('Request timeout - server took too long to respond. Please try again.');
    }
    // Handle network errors
    if (!error.response) {
      throw new Error('Network error - please check your internet connection and try again.');
    }
    throw error;
  }
};

export const getSignalsHistory = async () => {
  const response = await axios.get(`${API}/premium/signals/history`);
  return response.data;
};

// Signal Features APIs
export const getMarketSession = async () => {
  const response = await axios.get(`${API}/premium/signals/market-session`);
  return response.data;
};

export const getSignalPerformance = async () => {
  const response = await axios.get(`${API}/premium/signals/performance`);
  return response.data;
};

// AI Smart Insights - Signal Learning Analytics
export const getSignalInsights = async (days = 30) => {
  const response = await axios.get(`${API}/admin/signals/insights?days=${days}`);
  return response.data;
};

export const getSignalComparison = async (pair, timeframe) => {
  const response = await axios.get(`${API}/premium/signals/compare/${encodeURIComponent(pair)}/${timeframe}`);
  return response.data;
};

// AI Trade Autopsy (DEPRECATED)
export const analyzeTradeAutopsy = async (tradeId) => {
  const response = await axios.post(`${API}/premium/autopsy`, {
    trade_id: tradeId
  });
  return response.data;
};

export const getAutopsyHistory = async () => {
  const response = await axios.get(`${API}/premium/autopsy/history`);
  return response.data;
};

// Telegram Bot
export const connectTelegram = async (chatId, username = '') => {
  const response = await axios.post(`${API}/premium/telegram/connect`, {
    chat_id: chatId,
    username
  });
  return response.data;
};

export const disconnectTelegram = async () => {
  const response = await axios.post(`${API}/premium/telegram/disconnect`);
  return response.data;
};

export const getTelegramStatus = async () => {
  const response = await axios.get(`${API}/premium/telegram/status`);
  return response.data;
};

export const getTelegramBotInfo = async () => {
  const response = await axios.get(`${API}/premium/telegram/bot-info`);
  return response.data;
};

export const getTelegramSettings = async () => {
  const response = await axios.get(`${API}/premium/telegram/settings`);
  return response.data;
};

export const updateTelegramSettings = async (settings) => {
  const response = await axios.post(`${API}/premium/telegram/settings`, settings);
  return response.data;
};

export const testTelegramNotification = async () => {
  const response = await axios.post(`${API}/premium/telegram/test`);
  return response.data;
};

// Signal Preferences (Pairs & Timeframe Subscription)
export const getSignalPreferences = async () => {
  const response = await axios.get(`${API}/premium/signal-preferences`);
  return response.data;
};

export const updateSignalPreferences = async (preferences) => {
  const response = await axios.post(`${API}/premium/signal-preferences`, preferences);
  return response.data;
};

// User App Preferences (Theme, Language)
export const getUserPreferences = async () => {
  const response = await axios.get(`${API}/user/preferences`);
  return response.data;
};

export const updateUserPreferences = async (preferences) => {
  const response = await axios.post(`${API}/user/preferences`, preferences);
  return response.data;
};


// ============== ADMIN PANEL APIs ==============

// Track page visit for analytics
export const trackPageVisit = async (page, durationSeconds = 0) => {
  try {
    const response = await axios.post(`${API}/track/page-visit`, {
      user_id: 'default',
      page,
      duration_seconds: durationSeconds
    });
    return response.data;
  } catch (error) {
    console.error('Error tracking page visit:', error);
    return { success: false };
  }
};

// Admin: Get business intelligence stats
export const getAdminStats = async () => {
  const response = await axios.get(`${API}/admin/stats`);
  return response.data;
};

// Admin: Get all users
export const getAdminUsers = async () => {
  const response = await axios.get(`${API}/admin/users`);
  // API returns {users: [...]} - extract the users array
  return response.data?.users || response.data || [];
};

// Admin: Create user
export const createAdminUser = async (userData) => {
  const response = await axios.post(`${API}/admin/users`, userData);
  return response.data;
};

// Admin: Update user
export const updateAdminUser = async (userId, updateData) => {
  const response = await axios.put(`${API}/admin/users/${userId}`, updateData);
  return response.data;
};

// Admin: Delete user
export const deleteAdminUser = async (userId) => {
  const response = await axios.delete(`${API}/admin/users/${userId}`);
  return response.data;
};

// Admin: Extend user access
export const extendUserAccess = async (userId, days = 30) => {
  const response = await axios.post(`${API}/admin/users/${userId}/extend?days=${days}`);
  return response.data;
};

// Admin: Grant/Revoke Premium Status
export const setUserPremium = async (userId, isPremium, tier = 'premium', durationDays = 30) => {
  const response = await axios.post(`${API}/admin/users/${userId}/premium`, {
    is_premium: isPremium,
    tier: tier,
    duration_days: durationDays
  });
  return response.data;
};

// Admin: Get user sessions
export const getUserSessions = async (userId, days = 30) => {
  const response = await axios.get(`${API}/admin/users/${userId}/sessions?days=${days}`);
  return response.data;
};


// ============== ENHANCED ADMIN APIs ==============

// Payment & Subscription Management
export const getAdminPayments = async (days = 30) => {
  const response = await axios.get(`${API}/admin/payments`, { params: { days } });
  return response.data;
};

export const getAdminSubscriptions = async () => {
  const response = await axios.get(`${API}/admin/subscriptions`);
  return response.data;
};

export const getSubscriptionAlerts = async () => {
  const response = await axios.get(`${API}/admin/subscription-alerts`);
  return response.data;
};

// 1. Trading Behavior Analytics
export const getTradingBehaviorAnalytics = async () => {
  const response = await axios.get(`${API}/admin/analytics/trading-behavior`);
  return response.data;
};

export const getUserTradingAnalytics = async (userId) => {
  const response = await axios.get(`${API}/admin/analytics/user/${userId}/trading`);
  return response.data;
};

// 2. User Segmentation
export const getUserSegmentation = async () => {
  const response = await axios.get(`${API}/admin/segmentation`);
  return response.data;
};

// 3. Business Intelligence
export const getBusinessIntelligence = async () => {
  const response = await axios.get(`${API}/admin/business-intelligence`);
  return response.data;
};

// 4. Smart Alerts
export const getAdminAlerts = async () => {
  const response = await axios.get(`${API}/admin/alerts`);
  return response.data;
};

// 5. User Notes & Tags
export const getUserNotes = async (userId) => {
  const response = await axios.get(`${API}/admin/users/${userId}/notes`);
  return response.data;
};

export const addUserNote = async (userId, note) => {
  const response = await axios.post(`${API}/admin/users/${userId}/notes`, { note });
  return response.data;
};

export const deleteUserNote = async (noteId) => {
  const response = await axios.delete(`${API}/admin/notes/${noteId}`);
  return response.data;
};

export const getUserTags = async (userId) => {
  const response = await axios.get(`${API}/admin/users/${userId}/tags`);
  return response.data;
};

export const updateUserTags = async (userId, tags) => {
  const response = await axios.put(`${API}/admin/users/${userId}/tags`, { tags });
  return response.data;
};

export const getPresetTags = async () => {
  const response = await axios.get(`${API}/admin/tags/presets`);
  return response.data;
};


// ============== RETENTION APIs ==============

// A. Retention Dashboard
export const getRetentionDashboard = async () => {
  const response = await axios.get(`${API}/admin/retention/dashboard`);
  return response.data;
};

// B. Engagement Scores
export const getEngagementScores = async () => {
  const response = await axios.get(`${API}/admin/engagement/scores`);
  return response.data;
};

// C. Follow-up System
export const getFollowups = async () => {
  const response = await axios.get(`${API}/admin/followups`);
  return response.data;
};

export const createFollowup = async (data) => {
  const response = await axios.post(`${API}/admin/followups`, data);
  return response.data;
};

export const completeFollowup = async (followupId) => {
  const response = await axios.put(`${API}/admin/followups/${followupId}/complete`);
  return response.data;
};

export const deleteFollowup = async (followupId) => {
  const response = await axios.delete(`${API}/admin/followups/${followupId}`);
  return response.data;
};

export const getSuggestedFollowups = async () => {
  const response = await axios.get(`${API}/admin/followups/suggested`);
  return response.data;
};

// Contact History
export const getContactHistory = async (userId) => {
  const response = await axios.get(`${API}/admin/users/${userId}/contacts`);
  return response.data;
};

export const addContactLog = async (userId, data) => {
  const response = await axios.post(`${API}/admin/users/${userId}/contacts`, data);
  return response.data;
};

// ============== SIGNAL CACHE ADMIN ==============

export const getSignalCacheStatus = async () => {
  const response = await axios.get(`${API}/admin/signals/cache/all`);
  return response.data;
};

export const refreshSignalCache = async (pair = null, timeframe = null) => {
  const params = {};
  if (pair) params.pair = pair;
  if (timeframe) params.timeframe = timeframe;
  const response = await axios.post(`${API}/admin/signals/cache/refresh`, null, { params });
  return response.data;
};

// ============== WHATSAPP ALERTS ==============

export const getWhatsappStatus = async () => {
  const response = await axios.get(`${API}/premium/whatsapp/status`);
  return response.data;
};

export const connectWhatsapp = async (phoneNumber) => {
  const response = await axios.post(`${API}/premium/whatsapp/connect`, {
    phone_number: phoneNumber
  });
  return response.data;
};

export const disconnectWhatsapp = async () => {
  const response = await axios.post(`${API}/premium/whatsapp/disconnect`);
  return response.data;
};

export const toggleWhatsappAlerts = async (enabled) => {
  const response = await axios.post(`${API}/premium/whatsapp/toggle-alerts`, null, {
    params: { enabled }
  });
  return response.data;
};

export const testWhatsappNotification = async (message = null) => {
  const response = await axios.post(`${API}/premium/whatsapp/test`, {
    message: message
  });
  return response.data;
};

export const getWhatsappAlertSettings = async () => {
  const response = await axios.get(`${API}/premium/whatsapp/settings`);
  return response.data;
};

export const updateWhatsappAlertSettings = async (pairs, minConfidence, timeframes) => {
  const response = await axios.post(`${API}/premium/whatsapp/settings`, {
    pairs: pairs,
    min_confidence: minConfidence,
    timeframes: timeframes
  });
  return response.data;
};

// ============== SIGNAL PERFORMANCE TRACKING ==============

export const getSignalDailyReport = async (date = null) => {
  const params = date ? { date } : {};
  const response = await axios.get(`${API}/admin/signals/report/daily`, { params });
  return response.data;
};

export const getSignalWeeklyReport = async (week = null) => {
  const params = week ? { week } : {};
  const response = await axios.get(`${API}/admin/signals/report/weekly`, { params });
  return response.data;
};

export const getSignalMonthlyReport = async (month = null) => {
  const params = month ? { month } : {};
  const response = await axios.get(`${API}/admin/signals/report/monthly`, { params });
  return response.data;
};

export const getPendingSignals = async (limit = 50) => {
  const response = await axios.get(`${API}/admin/signals/pending`, { params: { limit } });
  return response.data;
};

export const getClosedSignals = async (limit = 20) => {
  const response = await axios.get(`${API}/admin/signals/closed`, { params: { limit } });
  return response.data;
};

export const updateSignalStatus = async (signalId, status, outcome, closePrice = null, pips = null, notes = null) => {
  const response = await axios.post(`${API}/admin/signals/update-status`, {
    signal_id: signalId,
    status,
    outcome,
    close_price: closePrice,
    pips,
    notes
  });
  return response.data;
};

export const bulkUpdateSignals = async (updates) => {
  const response = await axios.post(`${API}/admin/signals/bulk-update`, { updates });
  return response.data;
};

export const getAutoCheckStatus = async () => {
  const response = await axios.get(`${API}/admin/signals/auto-check/status`);
  return response.data;
};

export const triggerAutoCheckManually = async () => {
  const response = await axios.post(`${API}/admin/signals/auto-check/run`);
  return response.data;
};

export const getWinRateDashboard = async (days = 30) => {
  const response = await axios.get(`${API}/admin/signals/win-rate-dashboard`, { params: { days } });
  return response.data;
};

export const getVolumeProfile = async (symbol = 'XAU/USD', timeframe = 'H1') => {
  const response = await axios.get(`${API}/premium/volume-profile`, { params: { symbol, timeframe } });
  return response.data;
};

// ============== MARKETING ANGLE ==============

export const getMarketingAngle = async () => {
  const response = await axios.get(`${API}/marketing-angle`);
  return response.data;
};

// ============== WALLET SYSTEM ==============

export const getWalletBalance = async () => {
  const response = await axios.get(`${API}/wallet/balance`);
  return response.data;
};

export const getWalletConfig = async () => {
  const response = await axios.get(`${API}/wallet/config`);
  return response.data;
};

export const topupWallet = async (amount) => {
  const response = await axios.post(`${API}/wallet/topup`, { amount });
  return response.data;
};

export const confirmWalletTopup = async (sessionId) => {
  const response = await axios.post(`${API}/wallet/topup/confirm`, { session_id: sessionId });
  return response.data;
};

export const getWalletTransactions = async (limit = 20, skip = 0) => {
  const response = await axios.get(`${API}/wallet/transactions`, { params: { limit, skip } });
  return response.data;
};

export const toggleAutoRenew = async (enabled) => {
  const response = await axios.post(`${API}/wallet/auto-renew`, { enabled });
  return response.data;
};

// Admin Wallet Functions
export const adminCreditWallet = async (userId, amount, reason) => {
  const response = await axios.post(`${API}/admin/wallet/credit`, {
    user_id: userId,
    amount: amount,
    reason: reason
  });
  return response.data;
};

export const adminGetAllWallets = async () => {
  const response = await axios.get(`${API}/admin/wallet/all`);
  return response.data;
};

export const adminProcessRenewals = async () => {
  const response = await axios.post(`${API}/admin/wallet/process-renewals`);
  return response.data;
};

export const adminSendReminders = async () => {
  const response = await axios.post(`${API}/admin/wallet/send-reminders`);
  return response.data;
};
